// ============================================================================
// GENERIC DOCUMENT ANALYZER ENGINE
// ============================================================================

import { callClaude, parseJsonResponse, type ClaudeConfig } from '@/lib/ai/claude'
import { FACET_MAP, type FacetTypeDefinition } from './facetSchema'
import { buildSystemPrompt, buildUserPrompt, getDefaultEnabledFacets, getFacetsByIds } from './promptBuilder'
import type { AnalysisRequest, AnalysisResult, DetectedFacetResult, ContentType, ExtractedEntity, ExtractedRelationship } from './types'

// ============================================================================
// AI RESPONSE TYPES
// ============================================================================

interface AIFacetResponse {
  facetTypeId: string
  title?: string
  content: string
  evidence: string
  confidence: number
  suggestedEntityType?: string
  suggestedReason?: string
  problemStatement?: {
    context: string
    cause: string
    consequence: string
  }
}

interface AIEntityResponse {
  id: string
  type: string
  name: string
  description: string
  evidence: string
  confidence: number
}

interface AIRelationshipResponse {
  from: string
  to: string
  type: string
  evidence: string
  confidence: number
}

interface AIAnalysisResponse {
  entities?: AIEntityResponse[]
  relationships?: AIRelationshipResponse[]
  facets: AIFacetResponse[]
}

// ============================================================================
// ANALYZER FUNCTIONS
// ============================================================================

/**
 * Analyze content and detect facets using Claude AI
 */
export async function analyzeContent(
  config: ClaudeConfig,
  request: AnalysisRequest
): Promise<AnalysisResult> {
  const startTime = Date.now()

  // Get enabled facets (either from request or default)
  const enabledFacets = request.enabledFacetIds
    ? getFacetsByIds(request.enabledFacetIds)
    : getDefaultEnabledFacets()

  if (enabledFacets.length === 0) {
    return {
      sourceId: request.sourceId,
      facets: [],
      analysisTime: Date.now() - startTime,
    }
  }

  // Build prompts
  const systemPrompt = buildSystemPrompt(enabledFacets, request.contentType)
  const userPrompt = buildUserPrompt(request.content)

  // Call Claude AI
  let response: string
  try {
    response = await callClaude(config, systemPrompt, userPrompt)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown API error'
    console.error('[genericAnalyzer] Claude API call failed:', errorMessage)
    return {
      sourceId: request.sourceId,
      facets: [],
      analysisTime: Date.now() - startTime,
      error: errorMessage,
    }
  }

  // Parse response
  console.log('[genericAnalyzer] Raw response length:', response.length)
  console.log('[genericAnalyzer] Raw response preview:', response.slice(0, 1000))

  const parsed = parseJsonResponse<AIAnalysisResponse>(response)

  if (!parsed || !Array.isArray(parsed.facets)) {
    const errorMessage = 'Failed to parse AI response. The response may be malformed.'
    console.error('[genericAnalyzer] ' + errorMessage, response.slice(0, 500))
    return {
      sourceId: request.sourceId,
      facets: [],
      analysisTime: Date.now() - startTime,
      error: errorMessage,
    }
  }

  // Debug: Log what was parsed
  console.log('[genericAnalyzer] Parsed response:')
  console.log('  - entities:', parsed.entities?.length || 0)
  console.log('  - relationships:', parsed.relationships?.length || 0)
  console.log('  - facets:', parsed.facets?.length || 0)
  if (parsed.entities && parsed.entities.length > 0) {
    console.log('  - First entity:', JSON.stringify(parsed.entities[0]))
  }

  // Validate and transform facets
  const validFacets = validateAndTransformFacets(parsed.facets, enabledFacets)

  // Extract entities and relationships from response
  const entities: ExtractedEntity[] = (parsed.entities || []).map(e => ({
    id: e.id,
    type: e.type,
    name: e.name,
    description: e.description,
    evidence: e.evidence,
    confidence: e.confidence,
  }))

  const relationships: ExtractedRelationship[] = (parsed.relationships || []).map(r => ({
    from: r.from,
    to: r.to,
    type: r.type,
    evidence: r.evidence,
    confidence: r.confidence,
  }))

  console.log(`[genericAnalyzer] Extracted: ${entities.length} entities, ${relationships.length} relationships, ${validFacets.length} facets`)

  return {
    sourceId: request.sourceId,
    facets: validFacets,
    entities,
    relationships,
    analysisTime: Date.now() - startTime,
  }
}

/**
 * Validate and transform AI response facets
 */
function validateAndTransformFacets(
  aiFacets: AIFacetResponse[],
  enabledFacets: FacetTypeDefinition[]
): DetectedFacetResult[] {
  const enabledFacetIds = new Set(enabledFacets.map(f => f.id))
  const validFacets: DetectedFacetResult[] = []

  for (const aiFacet of aiFacets) {
    // Skip if facet type is not in enabled list
    if (!enabledFacetIds.has(aiFacet.facetTypeId)) {
      console.warn(`[genericAnalyzer] Skipping unknown facet type: ${aiFacet.facetTypeId}`)
      continue
    }

    // Validate required fields
    if (!aiFacet.content || !aiFacet.evidence) {
      console.warn(`[genericAnalyzer] Skipping facet with missing content/evidence:`, aiFacet)
      continue
    }

    // Get facet definition for default entity type
    const facetDef = FACET_MAP.get(aiFacet.facetTypeId)
    const suggestedEntityType = aiFacet.suggestedEntityType || facetDef?.defaultEntityType || null

    // Normalize confidence to 0-1 range
    let confidence = aiFacet.confidence
    if (confidence > 1) confidence = confidence / 100  // Handle percentage format
    confidence = Math.min(1, Math.max(0, confidence))

    validFacets.push({
      facetTypeId: aiFacet.facetTypeId,
      content: aiFacet.content.trim(),
      evidence: aiFacet.evidence.trim(),
      confidence,
      suggestedEntityType: suggestedEntityType || undefined,
      suggestedReason: aiFacet.suggestedReason?.trim(),
    })
  }

  return validFacets
}

/**
 * Analyze content with streaming progress updates
 */
export async function analyzeContentWithProgress(
  config: ClaudeConfig,
  request: AnalysisRequest,
  onProgress: (status: string) => void
): Promise<AnalysisResult> {
  onProgress('Preparing analysis...')

  // Get enabled facets
  const enabledFacets = request.enabledFacetIds
    ? getFacetsByIds(request.enabledFacetIds)
    : getDefaultEnabledFacets()

  onProgress(`Analyzing with ${enabledFacets.length} facet types...`)

  const result = await analyzeContent(config, request)

  onProgress(`Found ${result.facets.length} facets in ${result.analysisTime}ms`)

  return result
}

/**
 * Re-analyze specific facets (for refinement)
 */
export async function reanalyzeFacets(
  config: ClaudeConfig,
  content: string,
  contentType: ContentType,
  facetTypeIds: string[]
): Promise<DetectedFacetResult[]> {
  const facets = getFacetsByIds(facetTypeIds)

  if (facets.length === 0) {
    return []
  }

  const systemPrompt = buildSystemPrompt(facets, contentType)
  const userPrompt = buildUserPrompt(content)

  const response = await callClaude(config, systemPrompt, userPrompt)
  const parsed = parseJsonResponse<AIAnalysisResponse>(response)

  if (!parsed || !Array.isArray(parsed.facets)) {
    return []
  }

  return validateAndTransformFacets(parsed.facets, facets)
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Estimate token count for content (rough estimate)
 */
export function estimateTokenCount(content: string): number {
  // Rough estimate: ~4 characters per token for English
  return Math.ceil(content.length / 4)
}

/**
 * Check if content is within analyzable limits
 */
export function isContentAnalyzable(content: string): { valid: boolean; reason?: string } {
  const minLength = 50
  const maxLength = 100000  // ~25k tokens

  if (content.length < minLength) {
    return {
      valid: false,
      reason: `Content too short (minimum ${minLength} characters)`,
    }
  }

  if (content.length > maxLength) {
    return {
      valid: false,
      reason: `Content too long (maximum ${maxLength} characters). Consider splitting the document.`,
    }
  }

  return { valid: true }
}

/**
 * Split long content into chunks for analysis
 */
export function splitContentForAnalysis(
  content: string,
  maxChunkSize: number = 50000
): string[] {
  if (content.length <= maxChunkSize) {
    return [content]
  }

  const chunks: string[] = []
  const paragraphs = content.split(/\n\n+/)

  let currentChunk = ''

  for (const para of paragraphs) {
    if (currentChunk.length + para.length + 2 > maxChunkSize) {
      if (currentChunk) {
        chunks.push(currentChunk.trim())
      }
      currentChunk = para
    } else {
      currentChunk += (currentChunk ? '\n\n' : '') + para
    }
  }

  if (currentChunk) {
    chunks.push(currentChunk.trim())
  }

  return chunks
}

/**
 * Merge facets from multiple chunks, removing duplicates
 */
export function mergeFacets(
  allFacets: DetectedFacetResult[][]
): DetectedFacetResult[] {
  const merged: DetectedFacetResult[] = []
  const seenContent = new Set<string>()

  for (const chunkFacets of allFacets) {
    for (const facet of chunkFacets) {
      // Simple deduplication based on content similarity
      const normalizedContent = facet.content.toLowerCase().trim()
      if (!seenContent.has(normalizedContent)) {
        seenContent.add(normalizedContent)
        merged.push(facet)
      }
    }
  }

  return merged
}
